import { useEffect, useState, useCallback } from "react";
import { api, PRIORITY_STYLES } from "@/lib/api";
import { useCategories } from "@/context/CategoriesContext";
import { Ticket as TicketIcon, Inbox, Sparkles, Send, Clock, CheckCircle2, PlusCircle, Loader2, StickyNote, Wrench, ArrowRight, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS = [
  { key: "aperto", label: "Aperto", cls: "bg-blue-100 text-blue-800" },
  { key: "in_lavorazione", label: "In lavorazione", cls: "bg-amber-100 text-amber-800" },
  { key: "risolto", label: "Risolto", cls: "bg-emerald-100 text-emerald-800" },
  { key: "chiuso", label: "Chiuso", cls: "bg-neutral-200 text-neutral-700" },
];
const STATUS_CLS = Object.fromEntries(STATUS.map((s) => [s.key, s.cls]));
const STATUS_LABEL = Object.fromEntries(STATUS.map((s) => [s.key, s.label]));

const EVENT_ICON = {
  received: Inbox, analyzed: Sparkles, created: PlusCircle,
  replied: Send, status: CheckCircle2, note: StickyNote, supplier: Wrench,
};

const fmtTs = (ts) => {
  try {
    return new Date(ts).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return ts; }
};

const TimelineItem = ({ ev, last }) => {
  const Icon = EVENT_ICON[ev.type] || Clock;
  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div className="h-8 w-8 rounded-full bg-[#007BFF]/10 border border-[#007BFF]/30 flex items-center justify-center shrink-0">
          <Icon className="h-4 w-4 text-[#007BFF]" />
        </div>
        {!last && <div className="w-px flex-1 bg-border my-1" />}
      </div>
      <div className="pb-6">
        <div className="text-sm font-medium leading-snug">{ev.label}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{fmtTs(ev.ts)}</div>
      </div>
    </div>
  );
};

const TicketTable = ({ items, onOpen, prefix }) => {
  const { catLabel, catStyle } = useCategories();
  return (
  <div className="bg-card border border-border rounded-md overflow-hidden">
    <div className="overflow-x-auto scroll-thin">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
            <th className="px-5 py-3 font-semibold">Riferimento</th>
            <th className="px-5 py-3 font-semibold">Oggetto</th>
            <th className="px-5 py-3 font-semibold">Mittente</th>
            <th className="px-5 py-3 font-semibold">Categoria</th>
            <th className="px-5 py-3 font-semibold">Priorità</th>
            <th className="px-5 py-3 font-semibold">Fornitore</th>
            <th className="px-5 py-3 font-semibold">Stato</th>
          </tr>
        </thead>
        <tbody>
          {items.map((t, i) => (
            <tr key={t.id} data-testid={`${prefix}-ticket-row-${i}`} onClick={() => onOpen(t)}
              className="border-b border-border last:border-0 hover:bg-secondary/60 cursor-pointer transition-colors duration-150">
              <td className="px-5 py-3.5 font-semibold text-[#007BFF]">{t.ref}</td>
              <td className="px-5 py-3.5 max-w-xs truncate">{t.subject}</td>
              <td className="px-5 py-3.5 text-muted-foreground">{t.sender_name}</td>
              <td className="px-5 py-3.5"><span className={`text-xs px-2 py-0.5 rounded border ${catStyle(t.category)}`}>{catLabel(t.category) || t.category}</span></td>
              <td className="px-5 py-3.5"><span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${PRIORITY_STYLES[t.priority]}`}>{t.priority}</span></td>
              <td className="px-5 py-3.5 text-muted-foreground">
                {t.supplier_name
                  ? <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#0056B3]"><Wrench className="h-3.5 w-3.5" />{t.supplier_name}</span>
                  : <span className="text-xs text-muted-foreground">—</span>}
              </td>
              <td className="px-5 py-3.5"><span className={`text-xs px-2 py-0.5 rounded ${STATUS_CLS[t.status] || "bg-neutral-200 text-neutral-700"}`}>{STATUS_LABEL[t.status] || t.status}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
  );
};

export default function Tickets() {
  const { catLabel, catStyle } = useCategories();
  const [tickets, setTickets] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [supplierId, setSupplierId] = useState("");
  const [supplierMsg, setSupplierMsg] = useState("");
  const [sendingSupplier, setSendingSupplier] = useState(false);

  const load = useCallback(() => api.get("/tickets").then((r) => setTickets(r.data)), []);
  const loadSuppliers = useCallback(() => api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {}), []);
  useEffect(() => { load(); loadSuppliers(); }, [load, loadSuppliers]);

  const openTicket = async (t) => {
    setOpen(true);
    setSelected(t);
    setSupplierId("");
    setSupplierMsg("");
    try {
      const r = await api.get(`/tickets/${t.id}`);
      setSelected(r.data);
    } catch (e) { console.error("Errore caricamento ticket:", e); }
  };

  const changeStatus = async (status) => {
    if (!selected || status === selected.status) return;
    setUpdating(true);
    try {
      const r = await api.put(`/tickets/${selected.id}/status`, { status });
      setSelected(r.data);
      load();
    } catch { toast.error("Errore nell'aggiornamento dello stato del ticket"); }
    finally { setUpdating(false); }
  };

  const deleteTicket = async () => {
    if (!selected) return;
    if (!window.confirm(`Eliminare definitivamente il ticket ${selected.ref || ""}? L'operazione non è reversibile.`)) return;
    setUpdating(true);
    try {
      await api.delete(`/tickets/${selected.id}`);
      toast.success("Ticket eliminato");
      setOpen(false);
      setSelected(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Impossibile eliminare il ticket"); }
    finally { setUpdating(false); }
  };

  const addNote = async () => {
    const text = note.trim();
    if (!text || !selected) return;
    setSavingNote(true);
    try {
      const r = await api.post(`/tickets/${selected.id}/note`, { note: text });
      setSelected(r.data);
      setNote("");
      toast.success("Nota aggiunta alla timeline");
    } catch { toast.error("Errore nell'aggiunta della nota"); }
    finally { setSavingNote(false); }
  };

  const sendToSupplier = async () => {
    if (!selected || !supplierId) return toast.error("Seleziona un fornitore");
    setSendingSupplier(true);
    try {
      const r = await api.post(`/tickets/${selected.id}/send-to-supplier`, { supplier_id: supplierId, message: supplierMsg });
      setSelected(r.data);
      setSupplierId("");
      setSupplierMsg("");
      toast.success(`Ticket inviato al fornitore ${r.data.supplier_name}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Invio al fornitore fallito"); }
    finally { setSendingSupplier(false); }
  };

  const history = [...(selected?.history || [])].sort((a, b) => new Date(a.ts) - new Date(b.ts));
  const active = tickets.filter((t) => t.status !== "chiuso");
  const closed = tickets.filter((t) => t.status === "chiuso");

  return (
    <div className="fade-in">
      <header className="px-4 sm:px-10 pt-6 sm:pt-10 pb-6 border-b border-border bg-card/50">
        <div className="text-xs uppercase tracking-[0.2em] text-[#007BFF] font-semibold mb-2">Automazione</div>
        <h1 className="font-head text-4xl font-extrabold tracking-tighter">Ticket & Attività</h1>
        <p className="text-muted-foreground mt-2">Assegna i ticket ai fornitori e monitora lo stato. I ticket chiusi sono raccolti in fondo.</p>
      </header>

      <div className="p-4 sm:p-10 space-y-10">
        {tickets.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground flex flex-col items-center gap-3">
            <TicketIcon className="h-10 w-10 opacity-40" />
            Nessun ticket. Crea un ticket dalla vista dettaglio di una comunicazione.
          </div>
        ) : (
          <>
            <section>
              <h2 className="text-sm font-bold uppercase tracking-wider text-foreground mb-3 flex items-center gap-2">
                <TicketIcon className="h-4 w-4 text-[#007BFF]" />Da gestire
                <span className="text-xs font-medium text-muted-foreground bg-secondary rounded-full px-2 py-0.5">{active.length}</span>
              </h2>
              {active.length === 0 ? (
                <div className="text-sm text-muted-foreground bg-card border border-border rounded-md p-6 text-center">Nessun ticket aperto. Ottimo lavoro! 🎉</div>
              ) : (
                <TicketTable items={active} onOpen={openTicket} prefix="active" />
              )}
            </section>

            {closed.length > 0 && (
              <section data-testid="closed-tickets-section">
                <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" />Ticket chiusi
                  <span className="text-xs font-medium text-muted-foreground bg-secondary rounded-full px-2 py-0.5">{closed.length}</span>
                </h2>
                <div className="opacity-70">
                  <TicketTable items={closed} onOpen={openTicket} prefix="closed" />
                </div>
              </section>
            )}
          </>
        )}
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent data-testid="ticket-timeline-panel" className="w-full sm:max-w-lg overflow-y-auto">
          {selected && (
            <div className="fade-in">
              <SheetHeader className="text-left">
                <div className="text-[#007BFF] font-semibold text-sm">{selected.ref}</div>
                <SheetTitle className="font-head text-2xl tracking-tight">{selected.subject}</SheetTitle>
                <SheetDescription className="sr-only">Dettaglio e storico del ticket {selected.ref}</SheetDescription>
              </SheetHeader>

              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className={`text-xs px-2 py-0.5 rounded border ${catStyle(selected.category)}`}>{catLabel(selected.category) || selected.category}</span>
                <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${PRIORITY_STYLES[selected.priority]}`}>{selected.priority}</span>
                {selected.supplier_name && <span className="text-xs px-2 py-0.5 rounded bg-[#007BFF]/10 text-[#0056B3] inline-flex items-center gap-1"><Wrench className="h-3 w-3" />{selected.supplier_name}</span>}
              </div>
              <p className="text-sm text-muted-foreground mt-2">Mittente: <span className="text-foreground font-medium">{selected.sender_name}</span></p>
              {selected.summary && <p className="text-sm mt-3 bg-secondary/60 border border-border rounded-md p-3 leading-relaxed">{selected.summary}</p>}

              {/* Assegna a fornitore */}
              <div className="mt-6 border border-[#007BFF]/25 rounded-md p-4 bg-[#007BFF]/[0.03]">
                <div className="text-xs uppercase tracking-[0.18em] font-semibold text-[#007BFF] mb-3 flex items-center gap-2"><Wrench className="h-4 w-4" />Invia al fornitore</div>
                {suppliers.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nessun fornitore in rubrica. Aggiungi i fornitori nella sezione <span className="font-medium text-foreground">Fornitori</span> per poterli assegnare.</p>
                ) : (
                  <div className="space-y-3">
                    <Select value={supplierId} onValueChange={setSupplierId}>
                      <SelectTrigger data-testid="supplier-select" className="rounded-md h-10 text-sm"><SelectValue placeholder="Scegli un fornitore…" /></SelectTrigger>
                      <SelectContent>
                        {suppliers.map((s) => (
                          <SelectItem key={s.id} value={s.id} className="text-sm">{s.name}{s.category ? ` · ${s.category}` : ""}{s.email ? "" : " (senza email)"}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Textarea data-testid="supplier-message" value={supplierMsg} onChange={(e) => setSupplierMsg(e.target.value)} rows={3}
                      placeholder="Messaggio per il fornitore (facoltativo). Il ticket verrà allegato automaticamente." className="rounded-md text-sm resize-none" />
                    <Button data-testid="send-to-supplier-button" onClick={sendToSupplier} disabled={sendingSupplier || !supplierId} size="sm" className="rounded-md bg-[#007BFF] hover:bg-[#0056B3]">
                      {sendingSupplier ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Invia al fornitore (email)
                    </Button>
                    <p className="text-[11px] text-muted-foreground flex items-center gap-1"><ArrowRight className="h-3 w-3" />L'invio via SMS sarà disponibile prossimamente.</p>
                  </div>
                )}
              </div>

              <div className="mt-6">
                <div className="text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground mb-2.5">Stato</div>
                <div className="flex flex-wrap gap-2">
                  {STATUS.map((s) => (
                    <button key={s.key} data-testid={`ticket-status-${s.key}`} onClick={() => changeStatus(s.key)} disabled={updating}
                      className={`text-xs px-3 py-1.5 rounded-md border transition-colors duration-150 ${selected.status === s.key ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-border text-muted-foreground hover:border-foreground/40"}`}>
                      {s.label}
                    </button>
                  ))}
                  {updating && <Loader2 className="h-4 w-4 animate-spin text-[#007BFF] self-center" />}
                </div>
                {selected.status === "chiuso" && (
                  <button data-testid="delete-ticket-button" onClick={deleteTicket} disabled={updating}
                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-red-600 hover:text-red-700 hover:underline disabled:opacity-50">
                    <Trash2 className="h-3.5 w-3.5" />Elimina ticket chiuso
                  </button>
                )}
              </div>

              <div className="mt-7">
                <div className="text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground mb-4">Storico del ticket</div>
                {history.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nessun evento registrato.</p>
                ) : (
                  <div data-testid="ticket-timeline">
                    {history.map((ev, idx) => (
                      <TimelineItem key={`${ev.ts}-${idx}`} ev={ev} last={idx === history.length - 1} />
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-7">
                <div className="text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground mb-3">Aggiungi nota di aggiornamento</div>
                <Textarea data-testid="ticket-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={3}
                  placeholder="Scrivi un aggiornamento sul ticket (es. contattato il fornitore, sopralluogo fissato…)"
                  className="rounded-md text-sm resize-none" />
                <Button data-testid="add-ticket-note-button" onClick={addNote} disabled={savingNote || !note.trim()} size="sm" className="mt-2 rounded-md bg-[#007BFF] hover:bg-[#0056B3]">
                  {savingNote ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <StickyNote className="h-4 w-4 mr-2" />}Aggiungi nota
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
